// ============================================================================
// Utilitários puros do RTB12 (Simples Nacional)
// ----------------------------------------------------------------------------
// Separados do componente React para permitir TESTE UNITÁRIO — aqui mora
// lógica de dinheiro/imposto (controle do limite do Simples), que a diretriz
// do projeto manda testar.
//
// ⚠️ Regra de negócio (Contabilidade, 2026-07-01) — DOIS controles distintos,
//    NÃO confundir:
//
//  1. CONTROLE DO LIMITE (R$ 3,6M sub-limite / R$ 4,8M limite): é por
//     ANO-CALENDÁRIO. O contador zera o faturamento em 31/dez e recomeça do
//     zero em 1º/jan. Logo, o acúmulo SEMPRE parte de janeiro do ano vigente —
//     nunca uma janela rolante de 12 meses. É o que estas funções calculam.
//
//  2. BASE DA ALÍQUOTA (RBT12): soma dos 12 meses ANTERIORES ao mês de
//     apuração, SEM o mês vigente. Essa continua no componente
//     (`calcularRTB12Real`, sobre `periodoMesAnterior`) e NÃO usa este util.
// ============================================================================

// Período de trabalho em que a tela ABRE: o mês ANTERIOR ao corrente, no
// formato 'AAAA-MM' que o `<input type="month">` espera.
//
// Por que o mês anterior, e não o corrente: o RTB12 é alimentado por um
// balancete, e o balancete do mês em curso ainda não fechou. É a MESMA
// definição que a Home usa para cobrar a importação
// (`home/server-home.js`: `CURDATE() - INTERVAL 1 MONTH`) — se as duas
// divergissem, o card diria "importe 08/2026" e a tela abriria noutro mês.
// O Petty Cash e o Destaque do Mês seguem a mesma convenção.
//
// ⚠️ PEGADINHA DO DIA 31 (a razão de existir o construtor abaixo em vez de
//    `setMonth(getMonth() - 1)`): num dia 31, "menos 1 mês" cai num mês curto
//    que não tem dia 31 e o JS NORMALIZA para o mês seguinte — em 31/07,
//    31/06 vira 01/07 e o "mês anterior" sairia como julho, o próprio mês
//    corrente. Passar `1` como dia no construtor elimina o caso: o dia nunca
//    estoura. Já está documentado igual em `FinanceiroPettyCashControle.js`.
// ⚠️ A virada de ano sai de graça: `new Date(2026, -1, 1)` é dezembro/2025 —
//    o construtor normaliza mês negativo para o ano anterior.
export function periodoMesAnteriorAoCorrente(hoje = new Date()) {
    const ant = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    return `${ant.getFullYear()}-${String(ant.getMonth() + 1).padStart(2, '0')}`;
}

// Receita Bruta realizada de uma empresa num período (mês 'AAAA-MM').
// Retorna 0 se não houver registro de RECEITA para o par empresa/período.
export function obterReceitaBruta(dados, empresa, periodo) {
    const reg = dados.find(d => d.Empresa === empresa && d.Periodo === periodo && d.CC === 'RECEITA');
    return reg ? Number(reg.Credito) : 0;
}

// Soma o faturamento REALIZADO do ano-calendário `ano`, de janeiro (mês 1) até
// `mesAte` (inclusive), consolidando a lista de empresas informada.
// `mesAte === 0` retorna 0 (útil p/ "acúmulo até o mês anterior" quando o mês
// vigente é janeiro — não há meses fechados no ano ainda).
export function acumuladoAnoRealizado(dados, empresas, ano, mesAte) {
    let total = 0;
    for (let mes = 1; mes <= mesAte; mes++) {
        const periodo = `${ano}-${String(mes).padStart(2, '0')}`;
        // for (não forEach) para não declarar função dentro do loop (evita no-loop-func).
        for (let i = 0; i < empresas.length; i++) {
            total += obterReceitaBruta(dados, empresas[i], periodo);
        }
    }
    return total;
}

// Acúmulo do ano até o MÊS VIGENTE (inclusive) — a base do controle de limite.
// Meses já fechados entram pelo realizado; o mês vigente entra pelo realizado
// se já fechou (`temDadosReais`), senão pela projeção lançada (realizado + projeção).
export function acumuladoAnoAteVigente(dados, empresas, periodoAtual, temDadosReais, projecoesPorEmpresa = {}) {
    const [ano, mesAtual] = periodoAtual.split('-').map(Number);
    // meses fechados do ano: janeiro até o mês anterior ao vigente
    let total = acumuladoAnoRealizado(dados, empresas, ano, mesAtual - 1);
    // mês vigente
    if (temDadosReais) {
        const periodoVigente = `${ano}-${String(mesAtual).padStart(2, '0')}`;
        empresas.forEach(emp => { total += obterReceitaBruta(dados, emp, periodoVigente); });
    } else {
        empresas.forEach(emp => { total += (projecoesPorEmpresa[emp] || 0); });
    }
    return total;
}

// ============================================================================
// PROJEÇÃO AUTOMÁTICA e ENQUADRAMENTO (controle de limite)
// ----------------------------------------------------------------------------
// Regra fechada com a Contabilidade (2026-07-01):
//  - O limite anual do Simples é R$ 4,8M POR EMPRESA (por CNPJ) — não se
//    remaneja faturamento entre empresas.
//  - Queremos terminar o ano com uma SOBRA de no mínimo R$ 700 mil por empresa.
//    Isso vira um "teto efetivo" = LIMITE − sobraAlvo = R$ 4,1M, distribuído
//    LINEARMENTE nos 12 meses (ritmo mensal = teto efetivo / 12).
//  - A projeção do mês em aberto é o quanto ainda cabe até o teto acumulado do
//    período; nunca negativa (0 = a empresa já passou do ritmo → alerta).
// ============================================================================

// Thresholds do Simples (regra legal — fixos). RBA = Receita Bruta Acumulada no ano.
export const SUB_LIMITE_SIMPLES = 3600000;        // até aqui: 100% no DAS
export const LIMITE_SIMPLES = 4800000;            // até aqui: no Simples, ICMS/ISS por fora
export const TETO_DESENQUADRAMENTO = 5760000;     // acima daqui: desenquadra imediato (retroativo)

// Projeção automática do mês em aberto (`periodoAtual`), para UMA empresa.
// `limite` e `sobraAlvo` são parâmetros (default: limite do Simples e R$ 700 mil de sobra).
export function projecaoAutomatica(dados, empresa, periodoAtual, limite = LIMITE_SIMPLES, sobraAlvo = 700000) {
    const [ano, mes] = periodoAtual.split('-').map(Number);
    const ritmoMensal = (limite - sobraAlvo) / 12;
    const tetoPeriodo = ritmoMensal * mes;                          // N = mes (conta o mês em aberto)
    const rbaAnterior = acumuladoAnoRealizado(dados, [empresa], ano, mes - 1);
    return Math.max(0, tetoPeriodo - rbaAnterior);
}

// Último mês (1–12) do ano que já tem RECEITA lançada, para o conjunto de empresas.
// 0 = nenhum mês do ano tem dado ainda.
export function ultimoMesComReceita(dados, empresas, ano) {
    let ultimo = 0;
    for (let m = 1; m <= 12; m++) {
        const periodo = `${ano}-${String(m).padStart(2, '0')}`;
        if (dados.some(d => d.Periodo === periodo && d.CC === 'RECEITA' && empresas.includes(d.Empresa))) ultimo = m;
    }
    return ultimo;
}

// Projeção do RBA de FIM DE ANO no RITMO ATUAL: média mensal realizada × 12.
// É o "se continuar como está" — pode passar do limite (é justamente o alerta).
export function projecaoFimAnoRitmo(dados, empresas, ano, ultimoMes) {
    if (ultimoMes <= 0) return 0;
    const rba = acumuladoAnoRealizado(dados, empresas, ano, ultimoMes);
    return (rba / ultimoMes) * 12;
}

// Ritmo mensal atual e AJUSTE necessário para fechar o ano no alvo, por empresa.
//  - mediaMensal      = RBA realizado / meses com dado (o faturamento médio de hoje)
//  - permitidoMensal  = quanto pode faturar/mês nos meses restantes p/ fechar no alvo
//  - ajusteMensal     = quanto REDUZIR por mês (0 se a empresa está folgada)
// `alvoFechamento` costuma ser o teto efetivo (LIMITE − sobra-alvo), ex.: R$ 4,1M.
export function ritmoEAjuste(dados, empresa, ano, ultimoMes, alvoFechamento) {
    const rba = acumuladoAnoRealizado(dados, [empresa], ano, ultimoMes);
    const mediaMensal = ultimoMes > 0 ? rba / ultimoMes : 0;
    const mesesRestantes = 12 - ultimoMes;
    const permitidoMensal = mesesRestantes > 0 ? (alvoFechamento - rba) / mesesRestantes : 0;
    const ajusteMensal = mesesRestantes > 0 ? Math.max(0, mediaMensal - permitidoMensal) : 0;
    const projFimAnoRitmo = mediaMensal * 12;
    return { rba, mediaMensal, mesesRestantes, permitidoMensal, ajusteMensal, projFimAnoRitmo };
}

// Classifica o RBA da empresa nos patamares do Simples (para alerta/trava visual).
export function enquadramentoRBA(rba) {
    if (rba > TETO_DESENQUADRAMENTO) return { nivel: 'estouro', label: 'Desenquadramento imediato (retroativo)', cor: '#b91c1c' };
    if (rba > LIMITE_SIMPLES) return { nivel: 'excedido', label: 'Acima do limite — desenquadra em 1º/jan seguinte', cor: '#e11d48' };
    if (rba > SUB_LIMITE_SIMPLES) return { nivel: 'sublimite', label: 'Acima do sub-limite — ICMS/ISS por fora', cor: '#d97706' };
    return { nivel: 'ok', label: 'Dentro do sub-limite', cor: '#16a34a' };
}
